/**
 * TranscriptGroup Component
 * 
 * Displays a group of transcripts that belong to the same text object.
 * Features:
 * - Collapsible transcript list
 * - Group-level actions (shift, translate)
 * - Add transcript to group functionality
 * - Visual indicators for group timing
 */
import React, { useState } from 'react';
import {
    Paper,
    Box,
    Typography,
    Chip,
    IconButton,
    Tooltip,
    Button
} from '@mui/material';
import {
    Add,
    Translate,
    ExpandMore,
    ExpandLess,
    FindInPage
} from '@mui/icons-material';
import TranscriptCard from './TranscriptCard';
import { userRequest } from '../../requestMethods';
import TranslationMonitoringDialog from '../shared/TranslationMonitoringDialog';

const TranscriptGroup = ({ 
    group, 
    groupIndex, 
    onEdit, 
    onDelete, 
    onTranslate, 
    onShift, 
    onAddTranscript,
    formatTime 
}) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const [isTextExpanded, setIsTextExpanded] = useState(false);
    const [monitoringDialog, setMonitoringDialog] = useState({ open: false, loading: false, data: null });

    const handleCheckTranslations = async () => {
        if (!group?.allText?.text?._id) return;

        setMonitoringDialog({ open: true, loading: true, data: null });

        try {
            const response = await userRequest.get(`/text/check-translations/${group.allText.text._id}`);
            setMonitoringDialog({ open: true, loading: false, data: response.data });
        } catch (error) {
            console.error('Error checking translations:', error);
            
            // Handle 404 (no translation found) specifically
            if (error.response?.status === 404) {
                setMonitoringDialog({ 
                    open: true, 
                    loading: false, 
                    data: { 
                        error: 'No translation found for this transcript group. You need to create a translation first.',
                        hasUntranslated: false,
                        untranslatedWords: [],
                        untranslatedPhrases: [],
                        noTranslation: true
                    } 
                });
            } else {
                setMonitoringDialog({ 
                    open: true, 
                    loading: false, 
                    data: { 
                        error: 'Failed to check translations. Please try again.',
                        hasUntranslated: false,
                        untranslatedWords: [],
                        untranslatedPhrases: []
                    } 
                });
            }
        }
    };

    const handleCloseMonitoringDialog = () => {
        setMonitoringDialog({ open: false, loading: false, data: null });
    };

    const handleTranslateGroup = () => {
        onTranslate({ text: { _id: group.allText.text._id } });
    };

    return (
        <Paper sx={{ mb: 3, border: 1, borderColor: 'divider' }}>
            <Box sx={{ p: 2, backgroundColor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                        Text Group {groupIndex + 1} 
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                            ({formatTime(group.allText.start_seconds)} - {formatTime(group.allText.end_seconds)})
                        </Typography>
                    </Typography>
                    
                    <Box display="flex" alignItems="center" gap={1}>
                        <Chip 
                            label={`${group.transcripts.length} segments`} 
                            size="small" 
                            variant="outlined" 
                        />
                        {/* <Tooltip title="Shift Times & Positions">
                            <IconButton
                                size="small"
                                color="secondary"
                                onClick={() => onShift(group)}
                            >
                                <Tune />
                            </IconButton>
                        </Tooltip> */}
                        <Tooltip title="Check Missing Translations">
                            <IconButton
                                size="small"
                                color="secondary"
                                onClick={handleCheckTranslations}
                                disabled={!group?.allText?.text?._id}
                            >
                                <FindInPage />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Translate Combined Text">
                            <IconButton
                                size="small"
                                color="primary"
                                onClick={handleTranslateGroup}
                            >
                                <Translate />
                            </IconButton>
                        </Tooltip>
                        <IconButton
                            size="small"
                            onClick={() => setIsExpanded(!isExpanded)}
                        >
                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                    </Box>
                </Box>
                
                {/* Expandable combined text content */}
                <Box sx={{ mt: 1 }}>
                    <Button
                        variant="text"
                        size="small"
                        onClick={() => setIsTextExpanded(!isTextExpanded)}
                        startIcon={isTextExpanded ? <ExpandLess /> : <ExpandMore />}
                        sx={{ 
                            textTransform: 'none', 
                            color: 'text.secondary',
                            p: 0,
                            minWidth: 'auto'
                        }}
                    >
                        {isTextExpanded ? 'Hide' : 'Show'} Combined Text
                    </Button>
                    
                    {isTextExpanded && (
                        <Box sx={{ 
                            mt: 1, 
                            p: 2, 
                            backgroundColor: 'background.paper',
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1
                        }}>
                            <Typography 
                                variant="body2" 
                                sx={{ 
                                    lineHeight: 1.6,
                                    whiteSpace: 'pre-wrap',
                                    fontFamily: 'monospace'
                                }}
                            >
                                {group.combinedTextContent}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
            
            {isExpanded && (
                <Box sx={{ p: 2 }}>
                    {group.transcripts.map((transcript, index) => (
                        <TranscriptCard
                            key={transcript._id}
                            transcript={transcript}
                            index={index}
                            combinedText={group.combinedTextContent}
                            onEdit={onEdit}
                            onDelete={onDelete}
                            formatTime={formatTime}
                        />
                    ))}
                    
                    {/* Add transcript to this group button */}
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Add />}
                            onClick={() => onAddTranscript(group)}
                            sx={{ borderRadius: 2 }}
                        >
                            Add Transcript to Group
                        </Button>
                    </Box>
                </Box>
            )}

            {/* Translation Monitoring Dialog */}
            <TranslationMonitoringDialog
                open={monitoringDialog.open}
                onClose={handleCloseMonitoringDialog}
                loading={monitoringDialog.loading}
                data={monitoringDialog.data}
                onFixInEditor={handleTranslateGroup}
            />
        </Paper>
    );
};

export default TranscriptGroup; 